// Analytics.js
//
// Single chokepoint for PostHog product analytics. GA4 (react-ga4) handles
// marketing/UTM attribution only; do NOT add behavioral events to GA4 — they
// go through this module exclusively.
//
// Spec: docs/superpowers/specs/2026-04-30-posthog-integration-design.md

import posthog from 'posthog-js';

// ─── Sampling ──────────────────────────────────────────────────────────────

const PERSON_RATE = parseFloat(process.env.REACT_APP_POSTHOG_SAMPLE_RATE ?? '1.0');
const AUTOCAPTURE_RATE = parseFloat(process.env.REACT_APP_POSTHOG_AUTOCAPTURE_RATE ?? '0.25');

// Deterministic [0,1) bucketing on the first 8 hex chars of an id.
// `id` is expected to be a hex string (PostHog distinct_id or our SHA-256 hash).
// Applies a Murmur3-inspired finalizer to ensure sequential ids distribute uniformly.
const inSample = (id, rate) => {
	if (rate >= 1) return true;
	if (rate <= 0) return false;
	if (typeof id !== 'string' || id.length < 8) return false;
	let hash = parseInt(id.slice(0, 8), 16) >>> 0;
	if (Number.isNaN(hash)) return false;
	// Bit-mixing to distribute sequential inputs uniformly across [0, 0xFFFFFFFF]
	hash = ((hash ^ (hash >>> 16)) * 0x45d9f3b) >>> 0;
	hash = ((hash ^ (hash >>> 16)) * 0x45d9f3b) >>> 0;
	hash = (hash ^ (hash >>> 16)) >>> 0;
	return hash / 0xFFFFFFFF < rate;
};

// SHA-256 of a string, returned as lowercase hex.
// Uses Web Crypto API directly — jsdom test env is polyfilled in src/setupTests.js.
const hashUserId = async (rawId) => {
	const buf = new TextEncoder().encode(String(rawId));
	const digest = await crypto.subtle.digest('SHA-256', buf);
	return Array.from(new Uint8Array(digest))
		.map(b => b.toString(16).padStart(2, '0'))
		.join('');
};

// Sampling decision cache — computed once per session per id so borderline
// users don't oscillate between sampled/not as their distinct_id may change
// (anonymous → identified) within a session.
const sampleCache = new Map();

const sampleEventOrDrop = (event) => {
	const distinctId = event?.properties?.distinct_id;
	if (!distinctId) return event;

	let decision = sampleCache.get(distinctId);
	if (!decision) {
		decision = {
			person: inSample(distinctId, PERSON_RATE),
			autocapture: inSample(distinctId + ':autocapture', AUTOCAPTURE_RATE),
		};
		sampleCache.set(distinctId, decision);
	}

	if (!decision.person) return null;

	const isAutocapture = (
		event.event === '$autocapture' ||
		event.event === '$pageview' ||
		event.event === '$pageleave'
	);
	if (isAutocapture && !decision.autocapture) return null;

	return event;
};

// ─── Lifecycle ─────────────────────────────────────────────────────────────
//
// Every public method is wrapped in try/catch so an SDK regression, malformed
// bundle, or transient runtime issue can never crash a user action that fired
// the call. Failures are logged once per category to avoid console spam if a
// pathological state causes repeat failures.

let _initialized = false;

const _warnOnce = (() => {
	const seen = new Set();
	return (key, ...args) => {
		if (seen.has(key)) return;
		seen.add(key);
		console.warn(...args);
	};
})();

export const initAnalytics = () => {
	if (_initialized) return;
	const key = process.env.REACT_APP_POSTHOG_KEY;
	if (!key) return; // Silent no-op when key missing (dev/test).

	try {
		posthog.init(key, {
			api_host: process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com',
			persistence: 'localStorage+cookie',
			autocapture: true,
			capture_pageview: true,
			capture_pageleave: true,
			respect_dnt: true,
			disable_session_recording: true,
			mask_all_element_attributes: true,
			before_send: sampleEventOrDrop,
			loaded: (ph) => ph.debug(false),
		});
		_initialized = true;
	} catch (err) {
		_warnOnce('init', 'PostHog init failed; analytics disabled for this session:', err);
	}
};

export const identifyUser = async (rawUserId) => {
	if (!_initialized || !rawUserId) return;
	try {
		const hashed = await hashUserId(rawUserId);
		// Re-evaluate sampling cache for the new distinct_id (anonymous → identified).
		// Events enqueued under the anonymous distinct_id retain their pre-identify
		// sampling decision — cross-identity funnel queries should use PostHog's
		// identity-merged person view, not raw events.
		sampleCache.delete(hashed);
		posthog.identify(hashed);
	} catch (err) {
		_warnOnce('identify', 'PostHog identify failed:', err);
	}
};

export const registerCatalog = (catalog) => {
	if (!_initialized || !catalog) return;
	try {
		posthog.register({
			catalog_id: catalog.id ?? null,
			catalog_name: catalog.name ?? null,
			tenant_id: catalog.organization_id ?? catalog.tenant_id ?? null,
		});
	} catch (err) {
		_warnOnce('register', 'PostHog register failed:', err);
	}
};

export const trackEvent = (name, props = {}) => {
	if (!_initialized || !name) return;
	try {
		posthog.capture(name, props);
	} catch (err) {
		_warnOnce(`track:${name}`, `PostHog capture failed for "${name}":`, err);
	}
};

export const resetAnalytics = () => {
	if (!_initialized) return;
	try {
		posthog.reset();
		sampleCache.clear();
	} catch (err) {
		_warnOnce('reset', 'PostHog reset failed:', err);
	}
};

// ─── Test surface ──────────────────────────────────────────────────────────

export const __testables__ = {
	inSample,
	hashUserId,
	sampleEventOrDrop,
	_resetSampleCache: () => sampleCache.clear(),
};
